import { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { House, Receipt, Package, Users, DotsThreeCircle, Bell, Plus } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import api, { fmtDate, logError } from "../lib/api";
import { SeverityIcon } from "../lib/display";
import { HelpWidget } from "./HelpWidget";

const NAV = [
  { to: "/accueil", label: "Accueil", icon: House, end: true },
  { to: "/ventes", label: "Ventes", icon: Receipt },
  { to: "/stock", label: "Stock", icon: Package, stockOnly: true },
  { to: "/clients", label: "Clients", icon: Users },
  { to: "/plus", label: "Plus", icon: DotsThreeCircle },
];

function Bells() {
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const navigate = useNavigate();
  const load = useCallback(() => api.get("/notifications").then((r) => setNotifs(r.data)).catch((e) => logError(e)), []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  const unread = notifs.filter((n) => !n.read);

  const markRead = async (n) => {
    await api.post(`/notifications/${n.id}/read`).catch(() => {});
    load();
    if (n.link) { setOpen(false); navigate(n.link); }
  };

  return (
    <div className="relative">
      <button data-testid="notifications-bell" onClick={() => setOpen(!open)} className="relative p-2 rounded-full hover:bg-white transition-colors">
        <Bell size={22} className="text-[#16233D]" />
        {unread.length > 0 && (
          <span data-testid="notifications-badge" className="absolute -top-0.5 -right-0.5 bg-[#B4432F] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
            {unread.length}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notifications-panel" className="absolute right-0 top-11 w-80 max-h-96 overflow-y-auto kcard z-50 p-2">
          <p className="px-3 py-2 text-sm font-semibold font-display">Notifications</p>
          {unread.length === 0 && <p className="px-3 pb-3 text-sm text-gray-500">Aucune alerte active.</p>}
          {unread.map((n) => (
            <button key={n.id} data-testid="notification-item" onClick={() => markRead(n)}
              className="w-full text-left px-3 py-2.5 rounded-xl hover:bg-[#F6F4EF] flex gap-2 items-start transition-colors">
              <SeverityIcon severity={n.severity} size={18} />
              <span>
                <span className="text-sm block">{n.message}</span>
                <span className="text-xs text-gray-400">{fmtDate(n.created_at)}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function EstablishmentPicker() {
  const { estId, pickEstablishment } = useAuth();
  const [ests, setEsts] = useState([]);
  useEffect(() => { api.get("/establishments").then((r) => setEsts(r.data)).catch(() => {}); }, []);
  if (ests.length < 2) return null;
  return (
    <select data-testid="establishment-selector" value={estId} onChange={(e) => pickEstablishment(e.target.value)}
      className="text-sm bg-white border border-[#E9E4DA] rounded-full px-3 py-1.5 max-w-[190px]">
      <option value="">Groupe (consolidé)</option>
      {ests.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
    </select>
  );
}

export default function Layout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const withStock = user?.company?.stock_mode !== "no_stock";
  const items = NAV.filter((n) => withStock || !n.stockOnly);
  const mirror = user?.mirror;

  return (
    <div className="min-h-screen flex">
      {mirror && (
        <div data-testid="mirror-banner" className="fixed top-0 left-0 right-0 z-[60] bg-[#B4432F] text-white px-4 py-2 text-xs sm:text-sm flex items-center justify-between gap-3 flex-wrap">
          <span>
            Vous consultez le compte de <strong>{mirror.company_name}</strong> — Ticket #{mirror.ticket_no} ·{" "}
            {mirror.level === "full" ? "Contrôle complet" : "Lecture seule"}
          </span>
          <button data-testid="mirror-exit-btn" onClick={async () => {
            await api.post("/admin/mirror/stop").catch(() => {});
            window.location.href = "/admin";
          }} className="underline font-semibold">Quitter la vue miroir</button>
        </div>
      )}
      <aside className="hidden md:flex flex-col w-60 shrink-0 bg-[#16233D] text-white min-h-screen fixed left-0 top-0 bottom-0 p-5">
        <p className="font-display font-bold text-3xl lowercase tracking-tight mb-1">kavizio</p>
        <p className="text-xs text-white/50 mb-8 truncate">{user?.company?.name}</p>
        <nav className="flex flex-col gap-1">
          {items.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} data-testid={`nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors ${isActive ? "bg-white/10 font-semibold" : "text-white/70 hover:bg-white/5"}`}>
              <Icon size={20} /> {label}
            </NavLink>
          ))}
        </nav>
        <button data-testid="new-sale-btn-sidebar" onClick={() => navigate("/ventes/nouvelle")}
          className="btn-amber mt-auto py-3 px-4 flex items-center justify-center gap-2 text-sm">
          <Plus size={18} weight="bold" /> Nouvelle vente
        </button>
      </aside>

      <div className={`flex-1 md:ml-60 pb-24 md:pb-8 ${mirror ? "pt-9" : ""}`}>
        <header className="sticky top-0 z-40 bg-[#F6F4EF] border-b border-[#E9E4DA] px-4 md:px-8 py-3 flex items-center justify-between">
          <p className="font-display font-bold text-2xl lowercase tracking-tight md:hidden">kavizio</p>
          <p className="hidden md:block text-sm text-gray-500">{user?.name}</p>
          <div className="flex items-center gap-2">
            <EstablishmentPicker />
            <Bells />
            <button data-testid="new-sale-btn-header" onClick={() => navigate("/ventes/nouvelle")}
              className="btn-amber py-2 px-4 flex items-center gap-1.5 text-sm md:hidden">
              <Plus size={16} weight="bold" /> Vente
            </button>
          </div>
        </header>
        <main className="px-4 md:px-8 py-5 max-w-6xl">
          <Outlet />
        </main>
      </div>

      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[#E9E4DA] flex z-40">
        {items.map(({ to, label, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end} data-testid={`bottomnav-${label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center gap-0.5 py-2.5 text-[11px] transition-colors ${isActive ? "text-[#C9793A] font-semibold" : "text-[#16233D]/60"}`}>
            {({ isActive }) => (<><Icon size={22} weight={isActive ? "fill" : "regular"} />{label}</>)}
          </NavLink>
        ))}
      </nav>
      <HelpWidget />
    </div>
  );
}
